import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input, Label } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Plus, ShieldCheck, Copy, Save, Trash2, Power, PowerOff, RefreshCw } from 'lucide-react';

interface MU { id: string; email: string; full_name: string; active: boolean; context: string; insurer_id?: string | null; broker_id?: string | null; role_name?: string; role_key?: string; last_login_at?: string | null; must_reset_password?: boolean; created_at?: string | null; created_by_email?: string | null }

const errMsg = (e: unknown, fb: string) =>
  (e as { response?: { data?: { detail?: string; error?: string } } })?.response?.data?.detail ||
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error || fb;

const selectCls = 'h-10 rounded-md border border-input bg-background px-3 text-sm font-body text-raptor-navy focus:outline-none focus:ring-2 focus:ring-raptor-blue/40';

export const MasterUsers: React.FC = () => {
  const { user: me } = useAuth();
  const [rows, setRows] = useState<MU[]>([]);
  const [filter, setFilter] = useState('');
  const [fContext, setFContext] = useState('');
  const [fRole, setFRole] = useState('');
  const [fTenant, setFTenant] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [created, setCreated] = useState<{ email: string; temp_password: string } | null>(null);
  const [del, setDel] = useState<MU | null>(null);

  const load = async (): Promise<void> => {
    setRefreshing(true);
    try { const r = await api.get<MU[]>('/master/users'); setRows(r.data); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  const createAdmin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form.email.trim() || !form.full_name.trim()) { toast.error('Name and email are required.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/master/admins', form);
      setCreated({ email: r.data.email, temp_password: r.data.temp_password });
      setCreating(false); setForm({ email: '', full_name: '', phone: '' });
      toast.success('Master Admin created.');
      await load();
    } catch (err) { toast.error(errMsg(err, 'Create failed.')); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u: MU): Promise<void> => {
    setRowBusy(u.id);
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? 'User deactivated.' : 'User reactivated.');
      await load();
    } catch (err) { toast.error(errMsg(err, 'Could not update user.')); }
    finally { setRowBusy(null); }
  };

  const doDelete = async (): Promise<void> => {
    if (!del) return;
    setRowBusy(del.id);
    try {
      await api.delete(`/users/${del.id}`);
      toast.success(`${del.email} permanently deleted.`);
      setDel(null);
      await load();
    } catch (err) { toast.error(errMsg(err, 'Could not delete user.')); }
    finally { setRowBusy(null); }
  };

  const roleOptions = useMemo(() => Array.from(new Set(rows.map((u) => u.role_name).filter(Boolean))).sort() as string[], [rows]);
  const tenantOptions = useMemo(() => Array.from(new Set(rows.map((u) => u.insurer_id || u.broker_id).filter(Boolean))).sort() as string[], [rows]);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    return rows.filter((u) => {
      if (q && ![u.email, u.full_name, u.role_name, u.insurer_id, u.broker_id].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))) return false;
      if (fContext && u.context !== fContext) return false;
      if (fRole && u.role_name !== fRole) return false;
      if (fTenant && (u.insurer_id || u.broker_id) !== fTenant) return false;
      if (fStatus === 'active' && !u.active) return false;
      if (fStatus === 'inactive' && u.active) return false;
      if (fStatus === 'reset_pending' && !u.must_reset_password) return false;
      return true;
    });
  }, [rows, filter, fContext, fRole, fTenant, fStatus]);

  const clearFilters = (): void => { setFilter(''); setFContext(''); setFRole(''); setFTenant(''); setFStatus(''); };
  const anyFilter = filter || fContext || fRole || fTenant || fStatus;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="master-users">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Global users</h2>
          <p className="text-sm text-raptor-navy/60 font-body">Every account on the platform, across all insurers and brokerages. Add platform-level Master Admins here, or find a user across tenants when investigating access.</p>
        </div>
        <Button variant="gradient" onClick={() => { setCreating(!creating); setCreated(null); }} data-testid="new-master-admin-btn"><Plus size={16} /> {creating ? 'Cancel' : 'Add Master Admin'}</Button>
      </div>

      {creating && (
        <Card className="border-raptor-blue/30">
          <CardHeader><CardTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-raptor-blue" /> New Master Admin</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={createAdmin} className="grid md:grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Full name</Label><Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="master-admin-name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="master-admin-email" /></div>
              <div className="space-y-1.5"><Label>Phone (optional)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="md:col-span-3 flex items-center justify-between">
                <p className="text-xs text-raptor-navy/60 font-body">A temporary password is generated; the admin must reset it on first sign-in.</p>
                <Button type="submit" variant="gradient" disabled={busy} data-testid="master-admin-submit"><Save size={14} /> Create Master Admin</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {created && (
        <Card className="border-raptor-blue/40 bg-raptor-blue/5" data-testid="master-admin-created">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div className="text-sm font-body text-raptor-navy/80">
              <b>{created.email}</b> created. Share this one-time password securely — it is shown only once.
              <div className="mt-1 font-tabular text-base text-raptor-navy">{created.temp_password}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(created.temp_password); toast.success('Copied'); }}><Copy size={14} /> Copy</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="space-y-3">
          <CardTitle className="flex items-center justify-between">
            <span>All users · {filtered.length}{anyFilter ? ` of ${rows.length}` : ''}</span>
            <Button size="sm" variant="outline" onClick={load} disabled={refreshing} data-testid="master-users-refresh"><RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh</Button>
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Input className="max-w-xs" placeholder="Filter by name, email, insurer…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="master-users-filter" />
            <select className={selectCls} value={fContext} onChange={(e) => setFContext(e.target.value)} data-testid="filter-context">
              <option value="">All contexts</option>
              <option value="master">Master</option>
              <option value="insurer">Insurer</option>
              <option value="broker">Broker</option>
              <option value="client">Client</option>
            </select>
            <select className={selectCls} value={fRole} onChange={(e) => setFRole(e.target.value)} data-testid="filter-role">
              <option value="">All roles</option>
              {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className={selectCls} value={fTenant} onChange={(e) => setFTenant(e.target.value)} data-testid="filter-tenant">
              <option value="">All tenants</option>
              {tenantOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className={selectCls} value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="filter-status">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="reset_pending">Reset pending</option>
            </select>
            {anyFilter && <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="filter-clear">Clear</Button>}
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-auto">
          <table className="w-full text-sm font-body">
            <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
              <tr>
                <th className="py-2 px-3 text-left">ID</th>
                <th className="py-2 px-3 text-left">Name</th>
                <th className="py-2 px-3 text-left">Email</th>
                <th className="py-2 px-3 text-left">Role</th>
                <th className="py-2 px-3 text-left">Context</th>
                <th className="py-2 px-3 text-left">Tenant</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3 text-left">Created by</th>
                <th className="py-2 px-3 text-left">Created</th>
                <th className="py-2 px-3 text-left">Last login</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={11} className="py-6 text-center text-raptor-navy/50">No users match your filter.</td></tr>}
              {filtered.map((u) => {
                const isSelf = me?.id === u.id;
                return (
                  <tr key={u.id} className="border-t border-border/60" data-testid={`master-user-row-${u.id}`}>
                    <td className="py-2 px-3 font-tabular text-xs">{u.id}</td>
                    <td className="py-2 px-3">{u.full_name || '—'}</td>
                    <td className="py-2 px-3">{u.email}</td>
                    <td className="py-2 px-3">{u.role_name || '—'}</td>
                    <td className="py-2 px-3"><Badge variant={u.context === 'master' ? 'navy' : u.context === 'broker' ? 'cyan' : 'blue'}>{u.context}</Badge></td>
                    <td className="py-2 px-3 font-tabular text-xs">{u.insurer_id || u.broker_id || '—'}</td>
                    <td className="py-2 px-3">{u.active ? <Badge variant="success">Active</Badge> : <Badge variant="muted">Inactive</Badge>}{u.must_reset_password && <Badge variant="warning" className="ml-1">reset pending</Badge>}</td>
                    <td className="py-2 px-3 text-xs">{u.created_by_email || '—'}</td>
                    <td className="py-2 px-3 font-tabular text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                    <td className="py-2 px-3 font-tabular text-xs">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {isSelf ? (
                          <span className="text-xs text-raptor-navy/40 italic pr-1">You</span>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" disabled={rowBusy === u.id} onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`} title={u.active ? 'Deactivate' : 'Reactivate'}>
                              {u.active ? <><PowerOff size={13} /> Deactivate</> : <><Power size={13} /> Activate</>}
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={rowBusy === u.id} onClick={() => setDel(u)} data-testid={`delete-user-${u.id}`} title="Delete permanently"><Trash2 size={13} /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {del && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDel(null)}>
          <div className="bg-white rounded-md max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="delete-user-dialog">
            <div className="p-5 space-y-3">
              <h3 className="font-headline text-xl text-raptor-navy flex items-center gap-2"><Trash2 size={18} className="text-red-600" /> Delete user</h3>
              <p className="text-sm font-body text-raptor-navy/70">
                This permanently deletes <b>{del.full_name || del.email}</b> ({del.email}) and removes their sessions and any pending invites. This cannot be undone.
              </p>
              <p className="text-xs font-body text-raptor-navy/50">Prefer to keep the account? Use <b>Deactivate</b> instead — it blocks sign-in but keeps the record.</p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-border/60">
              <Button variant="outline" onClick={() => setDel(null)} data-testid="delete-user-cancel">Cancel</Button>
              <Button variant="destructive" onClick={doDelete} disabled={rowBusy === del.id} data-testid="delete-user-confirm"><Trash2 size={14} /> Delete permanently</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
